/**
 * Secure Capsule client-side cryptography (Web Crypto API, AES-GCM 256).
 *
 * The key is generated in the browser and NEVER sent to the server. It is
 * encoded into a shareable code the sender passes to the recipient out-of-band.
 * Requires a secure context (HTTPS or localhost) — window.crypto.subtle.
 */

const CODE_PREFIX = 'capsule:v1:';

function bytesToB64url(bytes) {
  let bin = '';
  const arr = new Uint8Array(bytes);
  for (let i = 0; i < arr.length; i++) bin += String.fromCharCode(arr[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64urlToBytes(b64url) {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
  const bin = atob(b64 + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function importKey(keyB64, usages) {
  return window.crypto.subtle.importKey(
    'raw', b64urlToBytes(keyB64), { name: 'AES-GCM' }, false, usages
  );
}

export function isSupported() {
  return typeof window !== 'undefined' && window.crypto && window.crypto.subtle && window.isSecureContext;
}

/**
 * Encrypt plaintext. Returns { iv, ciphertext } (base64url) for upload, and a
 * `code` string carrying capsuleId + key for the recipient (capsuleId filled in
 * by the caller after the server assigns it).
 */
export async function encryptCapsule(plaintext) {
  const key = await window.crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']
  );
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder().encode(plaintext);
  const ct = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc);
  const rawKey = await window.crypto.subtle.exportKey('raw', key);
  return {
    iv: bytesToB64url(iv),
    ciphertext: bytesToB64url(ct),
    keyB64: bytesToB64url(rawKey),
  };
}

/** Build the shareable code once the server has assigned a capsule id. */
export function buildCode(capsuleId, keyB64) {
  return `${CODE_PREFIX}${capsuleId}:${keyB64}`;
}

/** Parse a shareable code. Returns { capsuleId, keyB64 } or null. */
export function parseCode(code) {
  const trimmed = (code || '').trim();
  if (!trimmed.startsWith(CODE_PREFIX)) return null;
  const rest = trimmed.slice(CODE_PREFIX.length);
  const idx = rest.indexOf(':');
  if (idx < 0) return null;
  return { capsuleId: rest.slice(0, idx), keyB64: rest.slice(idx + 1) };
}

const VERIFIER_PLAINTEXT = 'arca-capsule-v1';

/**
 * A tiny blob encrypted under the capsule key and stored beside the ciphertext.
 *
 * It lets the recipient's browser test a key WITHOUT spending the capsule.
 * That matters because the claim destroys the ciphertext in the same operation
 * that hands it over, so a key has to be proven before the capsule is spent --
 * and a mistyped character does not change a key's length, so it slips past
 * every cheap structural check and would otherwise erase the message.
 *
 * The same pattern the Vault uses to tell a wrong passphrase from corrupted
 * data. It gives nothing away: a known plaintext under a 256-bit random
 * AES-GCM key is not a weakness, and the key itself still never reaches the
 * server.
 */
export async function makeVerifier(keyB64) {
  const key = await importKey(keyB64, ['encrypt']);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ct = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv }, key, new TextEncoder().encode(VERIFIER_PLAINTEXT)
  );
  return { iv: bytesToB64url(iv), ciphertext: bytesToB64url(ct) };
}

/** True when this key opens that verifier. Never throws -- a malformed code is
 *  simply a key that does not fit. */
export async function checkVerifier(keyB64, ivB64, ciphertextB64) {
  try {
    const key = await importKey(keyB64, ['decrypt']);
    const pt = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: b64urlToBytes(ivB64) }, key, b64urlToBytes(ciphertextB64)
    );
    return new TextDecoder().decode(pt) === VERIFIER_PLAINTEXT;
  } catch (e) {
    return false;
  }
}

/** Decrypt with a raw key (base64url), iv (base64url), ciphertext (base64url). */
export async function decryptCapsule(keyB64, ivB64, ciphertextB64) {
  const key = await importKey(keyB64, ['decrypt']);
  const pt = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: b64urlToBytes(ivB64) }, key, b64urlToBytes(ciphertextB64)
  );
  return new TextDecoder().decode(pt);
}

/**
 * Does this look like a real capsule key — 32 raw bytes, base64url?
 *
 * Worth asking before the capsule is spent. The server destroys the ciphertext
 * in the same operation that hands it over, so a code that was never going to
 * work has to be caught here rather than costing the member their message.
 */
export function isPlausibleKey(keyB64) {
  const k = (keyB64 || '').trim();
  if (!/^[A-Za-z0-9_-]+$/.test(k)) return false;
  try {
    return b64urlToBytes(k).length === 32;
  } catch (e) {
    return false;
  }
}
